<template>
  <div class="timeslot-stepper-container">

    <!-- Stepper Header -->
    <div>
      <div class="step-indicator mb-2">
        <span class="badge-step">Step {{ currentStep + 1 }} / {{ steps.length }}</span>
        <h5 class="d-inline-block ms-2 mb-0">{{ steps[currentStep].title }}</h5>
      </div>
    </div>

    <!-- Conversation Header -->
    <div class="conversation-header">
      <div class="avatar-assistant">
        <i class="fa-solid fa-user-doctor"></i>
      </div>
      <div class="assistant-bubble">
        <transition name="message-fade" mode="out-in">
          <div :key="currentStep" class="message-content">
            {{ conversationMessage }}
          </div>
        </transition>
      </div>
    </div>

    <!-- Main Content Area (Input Deck) -->
    <div class="step-content-card conversation-deck">
      <transition name="fade-slide" mode="out-in">

        <!-- Step 1: Days & Types -->
        <div v-if="currentStep === 0" key="step1" class="step-pane">
          <div class="section-block">
            <label class="section-label">1. Select Working Day</label>

            <!-- Auto-select all working days button -->
            <!-- <div class="auto-select-section mb-4">
              <button class="btn-theme-outline-primary" @click="autoSelectWorkingDays">
                <i class="fa-solid fa-wand-magic-sparkles me-2"></i>
                Auto-select from profile
              </button>
              <div class="text-muted small mt-2">
                <i class="fa-solid fa-info-circle me-1"></i>
                Will select: {{ autoSelectPreview }}
              </div>
            </div> -->

            <div class="days-grid">
              <div v-for="day in uniqueDays" :key="day" class="day-card" :class="{
                selected: selectedDays.includes(day),
                active: activeDay === day,
                'has-slots': hasAnySlotsForDay(day)
              }" @click="toggleDay(day)">
                <span class="day-name">{{ day }}</span>

                <!-- selected check -->
                <i v-if="selectedDays.includes(day)" class="fa-solid fa-check-circle day-check-icon"></i>

                <!-- active (editing) indicator -->
                <!-- <span v-if="activeDay === day" class="active-badge"><i class="fa-solid fa-circle"></i></span> -->

                <div v-if="hasAnySlotsForDay(day)" class="day-dot"></div>
              </div>
            </div>
          </div>

          <transition name="fade">
            <!-- Delete warning with premium feel -->
            <div
              v-if="selectedDays.length && selectedAppointmentTypes.length && hasSlotsForType(selectedAppointmentType)"
              class="mt-4 delete-block">
              <div class="premium-alert warning">
                <div class="alert-icon"><i class="fa-solid fa-trash-can"></i></div>
                <div class="alert-content">
                  <strong>Clear Slots?</strong>
                  <span>Remove all slots for {{ selectedDays.join(', ') }} - {{
                    formatAppointmentType(selectedAppointmentTypes.map(formatAppointmentType)).join(', ') }}
                  </span>
                </div>
                <button class="btn-theme-outline-danger" @click="deleteSelectedDayTypeSlot">Delete</button>
              </div>
            </div>
          </transition>

          <div class="section-block mt-5">
            <label class="section-label">2. Select Appointment Type</label>

            <!-- Auto-select all types button -->
            <!-- <div class="auto-select-section mb-4">
              <button class="btn-theme-outline-primary" @click="autoSelectAllTypes">
                <i class="fa-solid fa-layer-group me-2"></i>
                Select all appointment types
              </button>
            </div> -->

            <div class="types-grid">
              <!-- Loading State -->
              <div v-if="isLoading" class="text-center py-4 text-muted w-100">
                <i class="fa-solid fa-circle-notch fa-spin me-2"></i> Loading appointment types...
              </div>

              <div v-else v-for="typeObj in appointmentTypes" :key="typeObj.id" class="type-card" :class="{
                active: selectedAppointmentTypes.includes(typeObj.id),
                'auto-selected': autoSelectedTypes.includes(typeObj.id)
              }" @click="toggleType(typeObj.id)">
                <div class="type-icon-wrapper">
                  <i class="fa-solid fa-file-medical"></i>
                </div>
                <div class="type-info">
                  <span class="type-name">{{ typeObj.description || typeObj.label }}</span>
                  <span class="type-sub" v-if="hasSlotsForType(typeObj.id)">Configured</span>
                </div>
                <div class="custom-checkbox" :class="{ checked: selectedAppointmentTypes.includes(typeObj.id) }">
                </div>
              </div>
            </div>
          </div>

          <div v-if="stepErrors.types" class="error-box mt-4">
            <i class="fa-solid fa-circle-exclamation"></i> {{ stepErrors.types }}
          </div>
        </div>

        <!-- Step 2: Configure Slots -->
        <div v-else-if="currentStep === 1" key="step2" class="step-pane">
          <div class="config-header">
            <div>
              <p class="mb-3 text-muted">
                Configuring: <span class="highlight-text">{{ selectedDays.map(formatDay).join(', ') || 'None' }}</span>
                <span v-if="selectedAppointmentTypes.length"> • <span class="highlight-text-light">{{
                  selectedAppointmentTypes.map(formatAppointmentType).join(', ') }}</span></span>
              </p>

              <!-- Auto-populate from profile time button -->
              <div v-if="hasSittingTime" class="premium-alert info mb-4">
                <div class="alert-icon"><i class="fa-solid fa-clock"></i></div>
                <div class="alert-content">
                  <div><strong>Profile Sitting Time Available</strong></div>
                  <div class="small">Click below to auto-populate time settings from doctor profile:</div>
                  <button class="btn-theme-sm mt-2" @click="populateFromSittingTime">
                    <i class="fa-solid fa-bolt me-2"></i>
                    Auto-populate: {{ sittingTimeStart12hr }} - {{ sittingTimeEnd12hr }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!selectedAppointmentType && selectedAppointmentTypes.length" class="premium-alert info mb-4">
            <div class="alert-icon"><i class="fa-solid fa-hand-pointer"></i></div>
            <div class="alert-content">Tap a appointment type above to edit details.</div>
          </div>

          <div v-if="currentDay" class="config-form-wrapper">
            <div class="glass-panel">
              <h4 class="panel-title"><i class="fa-regular fa-clock"></i> Time Settings</h4>
              <div class="config-grid">
                <div class="form-group">
                  <label>Start Time</label>
                  <input type="time" class="form-control-theme" :value="currentDay.startTime"
                    @input="updateDayField('startTime', $event.target.value)" />
                </div>
                <div class="form-group">
                  <label>End Time</label>
                  <input type="time" class="form-control-theme" :value="currentDay.endTime"
                    @input="updateDayField('endTime', $event.target.value)" />
                </div>
                <div class="btn-generate">
                  <button class="btn-theme-primary" @click="generateSlotsLocal">
                    <i class="fa-solid fa-wand-magic"></i> Auto-Generate
                  </button>
                </div>
                <!-- <div class="form-group">
                  <label>Patients</label>
                  <input type="number" min="1" class="form-control-theme" :value="currentDay.numberOfPatients"
                    @input="updateDayField('numberOfPatients', $event.target.value)" />
                </div>
                <div class="form-group">
                  <label>Break (min)</label>
                  <input type="number" min="0" class="form-control-theme" :value="currentDay.break"
                    @input="updateDayField('break', $event.target.value)" />
                </div> -->
              </div>

              <div class="btn-generate">
                <!-- <button class="btn-theme-secondary" @click="createSingleSlot" :disabled="!canCreateSingle">
                  <i class="fa-solid fa-plus"></i> Single Slot
                </button> -->
                <!-- <button class="btn-theme-primary" @click="generateSlotsLocal">
                  <i class="fa-solid fa-wand-magic"></i> Auto-Generate
                </button> -->
                <!-- New button for auto-generating with 15-min intervals -->
                <!-- <button class="btn-theme-success" @click="autoGenerateAll15Min" :disabled="!canAutoGenerateAll">
                  <i class="fa-solid fa-bolt-lightning"></i> Auto-All (15 min)
                </button> -->
              </div>
            </div>

            <div class="available-slots-section mt-5">
              <h4 class="panel-title">Generated Slots <span class="badge-count"
                  v-if="currentDay.slots && currentDay.slots.length">{{ currentDay.slots.length }}</span></h4>

              <transition-group name="list" tag="div" class="slots-container"
                v-if="currentDay.slots && currentDay.slots.length">
                <div v-for="(slot, idx) in currentDay.slots" :key="slot.startTime + idx" class="slot-pill">
                  <span class="slot-time">{{ formatTimeDisplay(slot.startTime) }} - {{ formatTimeDisplay(slot.endTime)
                  }}</span>
                  <span class="slot-duration">{{ getSlotDuration(slot) }} min</span>
                  <!-- <div class="slot-delete" @click="$emit('remove-slot', idx)">
                    <i class="fa-solid fa-times"></i>
                  </div> -->
                </div>
              </transition-group>

              <div v-else class="empty-state">
                <div class="empty-icon"><i class="fa-regular fa-calendar-xmark"></i></div>
                <p>No slots generated yet.</p>
              </div>
            </div>
          </div>
          <div v-if="stepErrors.config" class="error-box mt-4">
            <i class="fa-solid fa-circle-exclamation"></i> {{ stepErrors.config }}
          </div>
        </div>

        <!-- Step 3: Review -->
        <div v-else-if="currentStep === 2" key="step3" class="step-pane">
          <div class="review-section">
            <h4 class="panel-title">Your Weekly Schedule Summary</h4>

            <!-- Bulk operations section -->
            <!-- <div class="bulk-actions-section mb-4">
              <div class="premium-alert success">
                <div class="alert-icon"><i class="fa-solid fa-rocket"></i></div>
                <div class="alert-content">
                  <strong>Bulk Operations</strong>
                  <div class="small">Apply schedule to multiple days at once</div>
                </div>
                <button class="btn-theme-success" @click="applyToAllSelectedDays">
                  <i class="fa-solid fa-copy me-2"></i>
                  Apply to Selected Days
                </button>
              </div>
            </div> -->

            <div v-if="doctor.days && doctor.days.length === 0" class="empty-state">
              <div class="empty-icon"><i class="fa-regular fa-calendar-xmark"></i></div>
              <p>No schedule configured yet.</p>
              <button class="btn-theme-primary mt-3" @click="currentStep = 0">
                <i class="fa-solid fa-plus"></i> Configure Schedule
              </button>
            </div>

            <div v-else class="review-grid-modern">
              <div v-for="group in groupedWeekDays" :key="group.day" class="review-card grouped-card">
                <div class="review-card-header">
                  <span class="day">{{ group.day }}</span>
                  <span class="badge bg-primary text-white pill-badge">{{ group.types.length }} Services</span>
                </div>
                <div class="review-card-body p-0">
                  <div v-for="(typeSlot, idx) in group.types" :key="typeSlot.appointmentType" class="type-slot-row"
                    :class="{ 'border-bottom': idx < group.types.length - 1 }">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <span class="type-badge">{{ formatAppointmentType(typeSlot.appointmentType) }}</span>
                      <span class="slot-count success small" v-if="typeSlot.slots && typeSlot.slots.length">
                        <i class="fa-regular fa-clock"></i> {{ typeSlot.slots.length }} Slots
                      </span>
                    </div>

                    <div v-if="typeSlot.slots && typeSlot.slots.length"
                      class="d-flex justify-content-between align-items-center">
                      <span class="slot-range small text-muted">
                        {{ convertTo12Hour(typeSlot.slots[0].startTime) }} - {{
                          convertTo12Hour(typeSlot.slots[typeSlot.slots.length - 1].endTime) }}
                      </span>
                      <button class="btn-icon-sm" @click.stop="openDetailModal(typeSlot)" title="View Details">
                        <i class="fa-solid fa-eye"></i>
                      </button>
                    </div>
                    <div v-else class="text-muted small">No slots configured</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </transition>
    </div>

    <!-- Stepper Footer -->
    <div class="stepper-actions">
      <button class="btn-theme-nav prev" @click="prevStep">
        <i class="fa-solid fa-arrow-left"></i> Back
      </button>
      <div class="action-spacer"></div>
      <button v-if="currentStep < steps.length - 1" class="btn-theme-nav next" @click="nextStep">
        Next Step <i class="fa-solid fa-arrow-right"></i>
      </button>
      <button v-else class="btn-theme-nav finish" @click="finish">
        <i class="fa-solid fa-check"></i> Complete Schedule
      </button>
    </div>

    <!-- Slot Details Modal -->
    <transition name="fade">
      <div v-if="showDetailModal" class="slot-detail-modal-overlay" @click.self="closeDetailModal">
        <div class="slot-detail-modal">
          <div class="modal-header-theme">
            <h4 class="m-0 text-white">{{ selectedDetailDay.day }} - {{
              formatAppointmentType(selectedDetailDay.appointmentType) }}</h4>
            <button class="close-btn" @click="closeDetailModal">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
          <div class="modal-body-theme">
            <div v-if="selectedDetailDay.slots && selectedDetailDay.slots.length" class="modal-slots-grid">
              <div v-for="(slot, idx) in selectedDetailDay.slots" :key="idx" class="modal-slot-item">
                {{ formatTimeDisplay(slot.startTime) }} - {{ formatTimeDisplay(slot.endTime) }}
                <span class="slot-duration-badge">{{ getSlotDuration(slot) }}min</span>
              </div>
            </div>
            <div v-else class="text-muted text-center py-4">No slots available.</div>
          </div>
          <div class="modal-footer-theme">
            <button class="btn-theme-secondary" @click="closeDetailModal">Close</button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { DoctorTimeSlotService } from '@/services/doctor-time-slots.service';
import { AppointmentTypeService } from '@/services/appointmentType.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

export default {
  name: "AddDoctorTimeslots",

  props: {
    doctor: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    selectedDays: {
      type: Array,
      default: () => [],
      required: true
    },
    activeDay: {
      type: String,
      default: "",
    },
    sittingTimeStart: {  // Change from sittingTime to separate props
      type: String,
      default: "",
    },
    sittingTimeEnd: {
      type: String,
      default: "",
    },
  },
  emits: [
    "update:selected-day",
    "generate-slots",
    "remove-slot",
    "update:doctor",
    "delete-day-slots",
    "show-toast",
    "finished",
    "saveDoctor",
    "profile-back",
    "update:selected-days",
    "update:active-day"
  ],
  data() {
    return {
      currentStep: 0,
      steps: [
        { key: 'days', title: 'Days & Services' },
        { key: 'configure', title: 'Time & Slots' },
        { key: 'review', title: 'Review' },
      ],
      stepErrors: {},
      selectedAppointmentType: "",
      selectedAppointmentTypes: [],

      // Auto-selection tracking
      autoSelectedDays: [],
      autoSelectedTypes: [],

      // Modal State
      showDetailModal: false,
      selectedDetailDay: {},

      // Appointment Types
      appointmentTypes: [],
      isLoading: false,
    };
  },
  created() {
    this.fetchAppointmentTypes();
  },
  computed: {
    conversationMessage() {
      switch (this.currentStep) {
        case 0:
          return "Great! Now let's set up your detailed schedule. Which Days will you be working, and what Services will you offer?";
        case 1: {
          const day = this.activeDay || 'your selected day';
          return `Perfect! Now let's configure the time slots for ${day}. What are your working hours and patient capacity?`;
        }
        case 2:
          return "All set! Please review your weekly schedule below. If everything looks good, you can complete the setup.";
        default:
          return "Let's proceed.";
      }
    },

    hasSittingTime() {
      return this.sittingTimeStart && this.sittingTimeEnd;
    },

    sittingTimeStart12hr() {
      return this.sittingTimeStart ? this.convertTo12Hour(this.sittingTimeStart) : '';
    },

    sittingTimeEnd12hr() {
      return this.sittingTimeEnd ? this.convertTo12Hour(this.sittingTimeEnd) : '';
    },

    autoSelectPreview() {
      const days = this.doctor?.workingDays || [];
      if (days.length === 0) return 'No days selected in profile';
      if (days.length === 7) return 'All days';
      return days.join(', ');
    },

    canCreateSingle() {
      return this.activeDay && this.selectedAppointmentType && this.currentDay && this.currentDay.startTime && this.currentDay.endTime;
    },

    canGenerate() {
      return this.activeDay && this.selectedAppointmentType && this.currentDay && this.currentDay.startTime && this.currentDay.endTime && this.currentDay.numberOfPatients;
    },

    canAutoGenerateAll() {
      //   return this.activeDay && this.selectedAppointmentType && this.currentDay && this.currentDay.startTime && this.currentDay.endTime && this.currentDay.numberOfPatients;
      return this.activeDay && this.selectedAppointmentType && this.currentDay && this.currentDay.startTime && this.currentDay.endTime && !this.currentDay.numberOfPatients;
    },

    uniqueDays() {
      return [
        "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
      ];
    },

    availableTypesForSelectedDay() {
      if (!this.activeDay) return [];
      const dayTypes = this.doctor.days
        .filter((d) => d.day === this.activeDay && d.appointmentType)
        .map((d) => d.appointmentType);
      return [...new Set(dayTypes)];
    },

    currentDay() {
      if (!this.activeDay || !this.selectedAppointmentType) return null;

      let entry = this.doctor.days.find(
        d =>
          d.day === this.activeDay &&
          d.appointmentType === this.selectedAppointmentType
      );

      if (!entry) {
        entry = {
          day: this.activeDay,
          appointmentType: this.selectedAppointmentType,
          startTime: "",
          endTime: "",
          numberOfPatients: 0,
          break: 0,
          slots: [],
        };
      }

      return entry;
    },

    groupedWeekDays() {
      if (!this.doctor.days || !this.doctor.days.length) return [];

      const grouped = {};
      this.doctor.days.forEach(daySlot => {
        if (!grouped[daySlot.day]) {
          grouped[daySlot.day] = {
            day: daySlot.day,
            types: []
          };
        }
        grouped[daySlot.day].types.push(daySlot);
      });

      const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

      return Object.values(grouped).sort((a, b) => {
        return dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
      });
    },
  },
  watch: {
    selectedAppointmentTypes(newArr) {
      if (!newArr.length) {
        this.selectedAppointmentType = "";
        return;
      }
      if (!newArr.includes(this.selectedAppointmentType)) {
        this.selectedAppointmentType = newArr[0];
      }
    },

    selectedDay(newDay) {
      if (!newDay) return;
      const exists = this.doctor.days.some(
        (d) =>
          d.day === newDay && d.appointmentType === this.selectedAppointmentType
      );

      if (!exists) {
        const firstType =
          this.doctor.days.find((d) => d.day === newDay)?.appointmentType || "";
        this.selectedAppointmentType =
          firstType || this.selectedAppointmentTypes[0] || "";
      }
    },
  },
  methods: {
    convertToLocalTime(date, time) {
      if (!time) return null;

      let hours, minutes;

      // ISO format
      if (time.includes("T")) {
        const d = new Date(time);
        if (isNaN(d)) return null;
        hours = d.getUTCHours();
        minutes = d.getUTCMinutes();
      } else {
        const parts = time.split(":");
        if (parts.length < 2) return null;
        [hours, minutes] = parts.map(Number);
      }

      // subtract 6 hours
      hours = hours - 6;

      if (hours < 0) {
        hours += 24;
      }

      const localDate = new Date();
      localDate.setHours(hours);
      localDate.setMinutes(minutes);
      localDate.setSeconds(0);

      return localDate;
    },

    formatTimeDisplay(time) {
      if (!time) return "";

      const localDate = this.convertToLocalTime(null, time);
      if (!localDate) return "";

      return localDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    async fetchAppointmentTypes() {
      this.isLoading = true;
      try {
        const hospitalId = this.doctor?.hospitalId || this.$store.state.user.currentUser.objectId;
        const response = await AppointmentTypeService.getAll({ hospitalId });
        const data = response.data;
        if (data.status === 200) {
          this.appointmentTypes = data.data.data || [];
          // Pre-select first type if duplicates exist or just logic
        } else {
          this.$emit("show-toast", {
            type: "error",
            message: data.message || "Failed to fetch appointment types"
          });
        }
      } catch (error) {
        console.error("Error fetching appointment types:", error);
        this.$emit("show-toast", {
          type: "error",
          message: "An error occurred while fetching appointment types"
        });
      } finally {
        this.isLoading = false;
      }
    },

    // Auto-selection methods
    autoSelectWorkingDays() {
      const workingDays = this.doctor?.workingDays || [];
      if (workingDays.length === 0) {
        this.$emit('show-toast', {
          type: 'warning',
          message: 'No working days selected in doctor profile'
        });
        return;
      }

      this.autoSelectedDays = [...workingDays];

      // Set the first working day as selected
      if (workingDays.length > 0 && !this.activeDay) {
        this.$emit('update:selected-day', workingDays[0]);
      }

      this.$emit('show-toast', {
        type: 'success',
        message: `Auto-selected ${workingDays.length} day(s) from profile`
      });
    },

    autoSelectAllTypes() {
      if (!this.appointmentTypes.length) return;

      const allTypes = this.appointmentTypes.map(t => t.code);
      this.selectedAppointmentTypes = [...allTypes];
      this.autoSelectedTypes = [...allTypes];
      this.selectedAppointmentType = allTypes[0];

      this.$emit('show-toast', {
        type: 'success',
        message: 'All appointment types selected'
      });
    },

    populateFromSittingTime() {
      if (!this.hasSittingTime || !this.selectedAppointmentType) {
        this.$emit('show-toast', {
          type: 'warning',
          message: 'Please select a appointment type first'
        });
        return;
      }

      console.log('Populating from sitting time:', this.sittingTimeStart, this.sittingTimeEnd);

      this.updateDayField('startTime', this.sittingTimeStart);
      this.updateDayField('endTime', this.sittingTimeEnd);

      // Set default values
      this.updateDayField('numberOfPatients', 10);
      this.updateDayField('break', 0);

      this.$emit('show-toast', {
        type: 'success',
        message: 'Time settings populated from profile'
      });

      // Auto-generate slots after populating
      this.$nextTick(() => {
        this.autoGenerateAll15Min();
      });
    },

    // NEW: Auto-generate with 15-minute intervals
    autoGenerateAll15Min() {
      if (!this.selectedAppointmentType) {
        this.$emit('show-toast', {
          type: 'warning',
          message: 'Please select a appointment type first'
        });
        return;
      }

      if (!this.currentDay.startTime || !this.currentDay.endTime) {
        this.$emit('show-toast', {
          type: 'warning',
          message: 'Please set start and end times first'
        });
        return;
      }

      // Parse start and end times
      const start = this.timeToMinutes(this.currentDay.startTime);
      const end = this.timeToMinutes(this.currentDay.endTime);

      if (end <= start) {
        this.$emit('show-toast', {
          type: 'warning',
          message: 'End time must be after start time'
        });
        return;
      }

      const totalDuration = end - start;
      const slotDuration = 15; // Fixed 15-minute slots
      const breakTime = this.currentDay.break || 0;
      const numberOfPatients = this.currentDay.numberOfPatients || Math.floor(totalDuration / slotDuration);

      // Calculate how many 15-minute slots fit
      const availableSlots = Math.floor(totalDuration / slotDuration);
      const actualSlots = Math.min(availableSlots, numberOfPatients);

      if (actualSlots <= 0) {
        this.$emit('show-toast', {
          type: 'warning',
          message: 'Time range too short for 15-minute slots'
        });
        return;
      }

      // Generate slots
      const newSlots = [];
      let current = start;

      for (let i = 0; i < actualSlots; i++) {
        const slotStart = this.minutesToTime(current);
        const slotEnd = this.minutesToTime(current + slotDuration);

        newSlots.push({
          startTime: slotStart,
          endTime: slotEnd
        });

        current += slotDuration + breakTime;

        // Stop if we exceed the end time
        if (current + slotDuration > end) {
          break;
        }
      }

      // Update the day with new slots
      let updatedDays = [...this.doctor.days];
      const existingIndex = updatedDays.findIndex(
        d => d.day === this.activeDay && d.appointmentType === this.selectedAppointmentType
      );

      if (existingIndex !== -1) {
        updatedDays[existingIndex] = {
          ...updatedDays[existingIndex],
          startTime: this.currentDay.startTime,
          endTime: this.currentDay.endTime,
          numberOfPatients: this.currentDay.numberOfPatients,
          break: this.currentDay.break,
          slots: newSlots
        };
      } else {
        updatedDays.push({
          day: this.activeDay,
          appointmentType: this.selectedAppointmentType,
          startTime: this.currentDay.startTime,
          endTime: this.currentDay.endTime,
          numberOfPatients: this.currentDay.numberOfPatients,
          break: this.currentDay.break,
          slots: newSlots
        });
      }

      this.$emit('update:doctor', { ...this.doctor, days: updatedDays });

      this.$emit('show-toast', {
        type: 'success',
        message: `Generated ${newSlots.length} slots with 15-minute intervals`
      });
    },

    // Apply current configuration to all auto-selected days and types
    applyToAllSelectedDays() {
      if (!this.selectedAppointmentType || !this.currentDay) {
        this.$emit('show-toast', {
          type: 'warning',
          message: 'Please configure a day first'
        });
        return;
      }

      const daysToApply = this.autoSelectedDays.length > 0 ? this.autoSelectedDays : [this.activeDay];
      const typesToApply = this.autoSelectedTypes.length > 0 ? this.autoSelectedTypes : [this.selectedAppointmentType];

      let updatedDays = [...this.doctor.days];

      daysToApply.forEach(day => {
        typesToApply.forEach(type => {
          const existingIndex = updatedDays.findIndex(d => d.day === day && d.appointmentType === type);

          const newDayConfig = {
            day,
            appointmentType: type,
            startTime: this.currentDay.startTime,
            endTime: this.currentDay.endTime,
            numberOfPatients: this.currentDay.numberOfPatients,
            break: this.currentDay.break,
            slots: [...(this.currentDay.slots || [])]
          };

          if (existingIndex !== -1) {
            updatedDays[existingIndex] = newDayConfig;
          } else {
            updatedDays.push(newDayConfig);
          }
        });
      });

      this.$emit('update:doctor', { ...this.doctor, days: updatedDays });

      this.$emit('show-toast', {
        type: 'success',
        message: `Applied configuration to ${daysToApply.length} day(s) and ${typesToApply.length} type(s)`
      });
    },

    // Helper methods
    getSlotDuration(slot) {
      const start = this.timeToMinutes(slot.startTime);
      const end = this.timeToMinutes(slot.endTime);
      return end - start;
    },

    // Rest of existing methods remain the same...
    goToStep(idx) {
      if (idx === this.currentStep) return;
      if (idx < this.currentStep) {
        this.currentStep = idx;
        return;
      }
      const validators = [this.validateStep1, this.validateStep2];
      for (let i = this.currentStep; i < idx; i++) {
        const ok = validators[i]()
        if (!ok) return;
      }
      this.currentStep = idx;
    },

    nextStep() {
      const okMap = [this.validateStep1, this.validateStep2];
      const validator = okMap[this.currentStep];
      if (validator && !validator()) return;
      if (this.currentStep < this.steps.length - 1) this.currentStep++;
      console.log("values", this.selectedDays, this.selectedAppointmentTypes);
    },

    prevStep() {
      console.log(this.currentStep);
      if (this.currentStep === 0) this.$emit('profile-back');
      if (this.currentStep > 0) this.currentStep--;
    },

    validateStep1() {
      this.stepErrors.types = "";

      if (!this.selectedDays.length) {
        this.stepErrors.types = "Select at least one working day.";
        return false;
      }

      if (!this.selectedAppointmentTypes.length) {
        this.stepErrors.types = "Select at least one appointment type.";
        return false;
      }

      return true;
    },
    validateStep2() {
      this.stepErrors.config = '';
      if (!this.currentDay) {
        this.stepErrors.config = 'Select a day and appointment type to configure.';
        return false;
      }
      if (!this.currentDay.startTime || !this.currentDay.endTime) {
        this.stepErrors.config = 'Please configure time settings (Start & End Time).';
        return false;
      }
      if (this.currentDay.endTime <= this.currentDay.startTime) {
        this.stepErrors.config = 'End time must be later than start time.';
        return false;
      }
      //   if (this.currentDay.numberOfPatients <= 0) {
      //     this.stepErrors.config = 'Number of patients must be greater than 0.';
      //     return false;
      //   }
      if (this.currentDay.break < 0) {
        this.stepErrors.config = 'Break time cannot be negative.';
        return false;
      }
      return true;
    },

    toggleType(type) {
      const idx = this.selectedAppointmentTypes.indexOf(type);
      if (idx === -1) {
        this.selectedAppointmentTypes.push(type);
      } else {
        this.selectedAppointmentTypes.splice(idx, 1);
      }
      this.selectedAppointmentType = this.selectedAppointmentTypes.length ?
        this.selectedAppointmentTypes[this.selectedAppointmentTypes.length - 1] : '';
    },

    hasAnySlotsForDay(day) {
      const target = (day || "").toString().toLowerCase().trim();
      return (this.doctor.days || []).some((d) => {
        const dday = (d.day || "").toString().toLowerCase().trim();
        return dday === target && Array.isArray(d.slots) && d.slots.length > 0;
      });
    },

    hasSlotsForType(type) {
      const rec = (this.doctor.days || []).find(
        (d) => d.day === this.activeDay && d.appointmentType === type
      );
      return rec && Array.isArray(rec.slots) && rec.slots.length > 0;
    },

    formatAppointmentType(typeId) {
      if (!typeId) return "";
      const found = this.appointmentTypes.find(t => t.id === typeId);
      return found ? found.description || found.label : typeId;
    },

    formatDay(day) {
      if (!day) return '';
      return day.substring(0, 3);
    },

    createSingleSlot() {
      if (!this.activeDay || !this.selectedAppointmentType) {
        alert("Please select both a day and an appointment type first.");
        return;
      }

      // const dayRecord = this.doctor?.days?.find(
      //   (d) =>
      //     d.day === this.activeDay &&
      //     d.appointmentType === this.selectedAppointmentType
      // );

      // ... existing code ...
    },

    // ... [skipping to generateSlotsLocal logic in catch block or where the loop was ] ...

    // Moving directly to the generateSlotsLocal inside try/catch block
    // I will replace the block from `try {` to the end of `generateSlotsLocal` to be safe and clean.

    // async generateSlotsLocal() {
    //   if (this.selectedAppointmentTypes.length === 0) {
    //     this.$emit("show-toast", {
    //       type: "warning",
    //       message: "Please select at least one appointment type.",
    //     });
    //     return;
    //   }

    //   // Use currentDay for validation as it holds the active input values
    //   if (
    //     !this.currentDay || !this.currentDay.startTime || !this.currentDay.endTime
    //   ) {
    //     this.$emit("show-toast", {
    //       type: "warning",
    //       message: `Please complete time configuration (Start & End Time).`,
    //     });
    //     return;
    //   }

    //   // Prepare params and payload for server assignment
    //   const doctorId = this.doctor?.id || this.doctor?.doctorId || '';
    //   // Convert to UTC HH:mm
    //   const userOffset = dayjs().utcOffset();
    //   const baseDate = dayjs();

    //   const startTimeUtc = dayjs(`${baseDate.format("YYYY-MM-DD")}T${this.currentDay.startTime}`)
    //     .utcOffset(userOffset, true)
    //     .utc()
    //     .format("HH:mm");

    //   const endTimeUtc = dayjs(`${baseDate.format("YYYY-MM-DD")}T${this.currentDay.endTime}`)
    //     .utcOffset(userOffset, true)
    //     .utc()
    //     .format("HH:mm");

    //   const params = {
    //     startTime: startTimeUtc,
    //     endTime: endTimeUtc,
    //     doctorId,
    //   };

    //   const payload = {
    //     days: this.selectedDays && this.selectedDays.length ? this.selectedDays : [this.activeDay],
    //     startTime: startTimeUtc,
    //     endTime: endTimeUtc,
    //     doctorId,
    //     appointmentTypeIds: this.selectedAppointmentTypes || [],
    //   };

    //   try {
    //     const res = await DoctorTimeSlotService.assignTimeslots(params, payload);
    //     // User reports response structure: { final: [...] }
    //     const generated = (res && res.data && res.data.final) || (res && res.data) || [];

    //     // Merge server-generated slots into doctor.days
    //     let updated = [...(this.doctor.days || [])];

    //     if (Array.isArray(generated) && generated.length) {
    //       // Group slots by day
    //       const grouped = {};

    //       generated.forEach((item) => {
    //         const dayName = item.day || item.dayName || this.activeDay;
    //         if (!grouped[dayName]) grouped[dayName] = [];

    //         // Convert UTC to Local time
    //         // We use today's date for 'dummy' date part, as we only care about time string
    //         const baseDate = dayjs().format("YYYY-MM-DD");

    //         const startLocal = dayjs.utc(`${baseDate}T${item.startTime}`)
    //           .local()
    //           .format("HH:mm");

    //         const endLocal = dayjs.utc(`${baseDate}T${item.endTime}`)
    //           .local()
    //           .format("HH:mm");

    //         // Store slots along with the appointmentTypeIds they belong to
    //         grouped[dayName].push({
    //           startTime: startLocal,
    //           endTime: endLocal,
    //           id: item.id,
    //           appointmentTypeIds: item.appointmentTypeIds || []
    //         });
    //       });

    //       Object.keys(grouped).forEach((dayName) => {
    //         const items = grouped[dayName];
    //         // We need to distribute these items to the correct doctor.days entries based on appointmentType

    //         // First, find all unique appointmentTypeIds involved for this day
    //         // Or better, iterate over all available types in frontend (or just selected ones) and see if they have slots

    //         // Strategy: For each selected appointment type (ID), filter slots that have this ID in their list
    //         // We need to map selected appointment type strings/codes to IDs first?
    //         // The user inputs 'selectedAppointmentTypes' as array of Ids? Or codes?
    //         // In ToggleType and AutoSelect, it pushes `type`. `formatAppointmentType` looks for `t.label === type`.
    //         // It seems 'selectedAppointmentTypes' stores the LABEL or CODE? 
    //         // Wait, user payload provided: "appointmentTypeIds": ["694e...", ...]
    //         // If `this.selectedAppointmentTypes` held IDs, then simpler.
    //         // But `toggleType` pushes `type`. `appointmentTypes` usually have `id`.
    //         // I need to check if `selectedAppointmentTypes` holds strings (names) or IDs.
    //         // User prompt: "i can send multiple appointment ids after select... appointmentTypeIds: this.selectedAppointmentTypes || []".
    //         // This implies `selectedAppointmentTypes` holds IDs.
    //         // But `formatAppointmentType` does `t.label === type`. If type is ID, `t.label === ID` is wrong.
    //         // IF `selectedAppointmentTypes` holds IDs, then `t.id === type` would be correct.
    //         // Let's assume `selectedAppointmentTypes` holds IDs as per the payload requirement.

    //         // If they are IDs, we can match with response.

    //         this.selectedAppointmentTypes.forEach(typeId => {
    //           // Find slots in 'items' that contain this typeId
    //           const typeSlots = items.filter(slot =>
    //             slot.appointmentTypeIds && slot.appointmentTypeIds.includes(typeId)
    //           ).map(s => ({ startTime: s.startTime, endTime: s.endTime, id: s.id }));

    //           // If no slots from server for this type, maybe skip or use all?
    //           // If the server returns appointmentTypeIds, we should respect it.
    //           // If 'appointmentTypeIds' is missing in response item, maybe it applies to all?

    //           const finalSlots = typeSlots.length > 0 ? typeSlots.sort((a, b) => a.startTime.localeCompare(b.startTime)) : [];

    //           if (finalSlots.length === 0) return; // Don't update if no slots return for this type

    //           // We need to store the APPOINTMENT TYPE LABEL/CODE in doctor.days if that's what the UI expects?
    //           // The UI uses `formatAppointmentType` which finds by label?
    //           // If we are strictly using IDs now, we should ensure `doctor.days` stores ID too?
    //           // The `doctor.days` structure: `appointmentType`.
    //           // Let's stick to using the ID as the key in `doctor.days` if that's what we are using.

    //           const appointmentType = typeId;

    //           const idx = updated.findIndex(d => d.day === dayName && d.appointmentType === appointmentType);
    //           const newDay = {
    //             day: dayName,
    //             appointmentType,
    //             startTime: this.currentDay.startTime,
    //             endTime: this.currentDay.endTime,
    //             break: this.currentDay.break,
    //             numberOfPatients: this.currentDay.numberOfPatients,
    //             slots: finalSlots,
    //           };

    //           if (idx !== -1) updated[idx] = newDay; else updated.push(newDay);
    //         });
    //       });
    //     }

    //     this.$emit('update:doctor', { ...this.doctor, days: updated });
    //     this.$emit('show-toast', { type: 'success', message: `Slots generated from server.` });
    //   } catch (error) {
    //     console.error(error);
    //     this.$emit('show-toast', { type: 'error', message: (error && error.message) || 'Failed to generate slots from server' });
    //   }
    // },

    convertToServerTime(time) {
      if (!time) return null;

      let [hours, minutes] = time.split(":").map(Number);

      // ADD 6 hours (reverse of local)
      hours = hours + 6;

      if (hours >= 24) {
        hours -= 24;
      }

      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    },

    async generateSlotsLocal() {
      if (this.selectedAppointmentTypes.length === 0) {
        this.$emit("show-toast", {
          type: "warning",
          message: "Please select at least one appointment type.",
        });
        return;
      }

      if (!this.currentDay || !this.currentDay.startTime || !this.currentDay.endTime) {
        this.$emit("show-toast", {
          type: "warning",
          message: `Please complete time configuration (Start & End Time).`,
        });
        return;
      }

      const doctorId = this.doctor?.doctorId || '';

      // Convert local times to server time (ADD 6 hours for server)
      const startTimeUtc = this.convertToServerTime(this.currentDay.startTime);
      const endTimeUtc = this.convertToServerTime(this.currentDay.endTime);

      const params = {
        startTime: startTimeUtc,
        endTime: endTimeUtc,
        doctorId,
      };

      const payload = {
        days: this.selectedDays && this.selectedDays.length ? this.selectedDays : [this.activeDay],
        startTime: startTimeUtc,
        endTime: endTimeUtc,
        doctorId,
        appointmentTypeIds: this.selectedAppointmentTypes || [],
      };

      try {
        console.log("Payload:", payload);
        const res = await DoctorTimeSlotService.assignTimeslots(params, payload);
        console.log("Server response:", res);

        // Extract generated slots from response
        const generated = (res && res.data && res.data.data && res.data.data.final) ||
          (res && res.data && res.data.data) ||
          (res && res.data) ||
          [];

        console.log("Generated slots:", generated);

        // Merge server-generated slots into doctor.days
        let updated = [...(this.doctor.days || [])];

        if (Array.isArray(generated) && generated.length) {
          // Group slots by day
          const grouped = {};

          generated.forEach((item) => {
            const rawDay = item.day || item.dayName || this.activeDay;
            const dayName = this.normalizeDay(rawDay);
            if (!grouped[dayName]) grouped[dayName] = [];

            // IMPORTANT: Store RAW server times - NO CONVERSION
            // The display will use formatTimeDisplay which will subtract 6 hours
            grouped[dayName].push({
              startTime: item.startTime,  // Keep as "18:00"
              endTime: item.endTime,      // Keep as "18:15"
              id: item.id,
              appointmentTypeIds: item.appointmentTypeIds || []
            });
          });

          console.log("Grouped slots:", grouped);

          Object.keys(grouped).forEach((dayName) => {
            const items = grouped[dayName];

            this.selectedAppointmentTypes.forEach(typeId => {
              // Find slots that belong to this appointment type
              const typeSlots = items.filter(slot =>
                slot.appointmentTypeIds && slot.appointmentTypeIds.includes(typeId)
              ).map(s => ({
                startTime: s.startTime,  // Raw server time
                endTime: s.endTime,      // Raw server time
                id: s.id
              }));

              // Sort slots by start time
              const finalSlots = typeSlots.length > 0
                ? typeSlots.sort((a, b) => a.startTime.localeCompare(b.startTime))
                : [];

              if (finalSlots.length === 0) return;

              const appointmentType = typeId;
              const idx = updated.findIndex(d => d.day === dayName && d.appointmentType === appointmentType);

              const newDay = {
                day: dayName,
                appointmentType,
                startTime: this.currentDay.startTime,
                endTime: this.currentDay.endTime,
                break: this.currentDay.break,
                numberOfPatients: this.currentDay.numberOfPatients,
                slots: finalSlots,  // Raw server times
              };

              if (idx !== -1) {
                updated[idx] = newDay;
              } else {
                updated.push(newDay);
              }
            });
          });
        }

        this.$emit('update:doctor', { ...this.doctor, days: updated });
        this.$emit('show-toast', {
          type: 'success',
          message: `Slots generated from server.`
        });
      } catch (error) {
        console.error(error);
        this.$emit('show-toast', {
          type: 'error',
          message: (error && error.message) || 'Failed to generate slots from server'
        });
      }
    },

    // async generateSlotsLocal() {
    //   if (this.selectedAppointmentTypes.length === 0) {
    //     this.$emit("show-toast", {
    //       type: "warning",
    //       message: "Please select at least one appointment type.",
    //     });
    //     return;
    //   }

    //   if (!this.currentDay || !this.currentDay.startTime || !this.currentDay.endTime) {
    //     this.$emit("show-toast", {
    //       type: "warning",
    //       message: `Please complete time configuration (Start & End Time).`,
    //     });
    //     return;
    //   }

    //   const doctorId = this.doctor?.doctorId || '';

    //   // Convert local times to server time (UTC+6)
    //   const startTimeUtc = this.convertToServerTime(this.currentDay.startTime);
    //   const endTimeUtc = this.convertToServerTime(this.currentDay.endTime);

    //   const params = {
    //     startTime: startTimeUtc,
    //     endTime: endTimeUtc,
    //     doctorId,
    //   };

    //   const payload = {
    //     days: this.selectedDays && this.selectedDays.length ? this.selectedDays : [this.activeDay],
    //     startTime: startTimeUtc,
    //     endTime: endTimeUtc,
    //     doctorId,
    //     appointmentTypeIds: this.selectedAppointmentTypes || [],
    //   };

    //   try {
    //     console.log("Payload:", payload);
    //     const res = await DoctorTimeSlotService.assignTimeslots(params, payload);
    //     console.log("Server response:", res);

    //     // Extract generated slots from response
    //     const generated = (res && res.data && res.data.data && res.data.data.final) ||
    //       (res && res.data && res.data.data) ||
    //       (res && res.data) ||
    //       [];

    //     console.log("Generated slots:", generated);

    //     // Merge server-generated slots into doctor.days
    //     let updated = [...(this.doctor.days || [])];

    //     if (Array.isArray(generated) && generated.length) {
    //       // Group slots by day
    //       const grouped = {};

    //       generated.forEach((item) => {
    //         const rawDay = item.day || item.dayName || this.activeDay;
    //         const dayName = this.normalizeDay(rawDay);
    //         if (!grouped[dayName]) grouped[dayName] = [];

    //         // Convert server time (UTC+6) to LOCAL time for display
    //         // This uses the same convertToLocalTime function that subtracts 6 hours
    //         const startLocal = this.formatTimeForStorage(item.startTime);
    //         const endLocal = this.formatTimeForStorage(item.endTime);

    //         grouped[dayName].push({
    //           startTime: startLocal,
    //           endTime: endLocal,
    //           id: item.id,
    //           appointmentTypeIds: item.appointmentTypeIds || []
    //         });
    //       });

    //       console.log("Grouped slots:", grouped);

    //       Object.keys(grouped).forEach((dayName) => {
    //         const items = grouped[dayName];

    //         this.selectedAppointmentTypes.forEach(typeId => {
    //           // Find slots that belong to this appointment type
    //           const typeSlots = items.filter(slot =>
    //             slot.appointmentTypeIds && slot.appointmentTypeIds.includes(typeId)
    //           ).map(s => ({
    //             startTime: s.startTime,
    //             endTime: s.endTime,
    //             id: s.id
    //           }));

    //           // Sort slots by start time
    //           const finalSlots = typeSlots.length > 0
    //             ? typeSlots.sort((a, b) => a.startTime.localeCompare(b.startTime))
    //             : [];

    //           if (finalSlots.length === 0) return;

    //           const appointmentType = typeId;
    //           const idx = updated.findIndex(d => d.day === dayName && d.appointmentType === appointmentType);

    //           const newDay = {
    //             day: dayName,
    //             appointmentType,
    //             startTime: this.currentDay.startTime,
    //             endTime: this.currentDay.endTime,
    //             break: this.currentDay.break,
    //             numberOfPatients: this.currentDay.numberOfPatients,
    //             slots: finalSlots,
    //           };

    //           if (idx !== -1) {
    //             updated[idx] = newDay;
    //           } else {
    //             updated.push(newDay);
    //           }
    //         });
    //       });
    //     }

    //     this.$emit('update:doctor', { ...this.doctor, days: updated });
    //     this.$emit('show-toast', {
    //       type: 'success',
    //       message: `Slots generated from server.`
    //     });
    //   } catch (error) {
    //     console.error(error);
    //     this.$emit('show-toast', {
    //       type: 'error',
    //       message: (error && error.message) || 'Failed to generate slots from server'
    //     });
    //   }
    // },

    // Add this helper method to format time for storage (converts server time to local)
    formatTimeForStorage(serverTime) {
      if (!serverTime) return "";

      // Use convertToLocalTime which subtracts 6 hours to get local time
      const localDate = this.convertToLocalTime(null, serverTime);
      if (!localDate) return "";

      // Format as HH:mm for storage
      const hours = localDate.getHours().toString().padStart(2, "0");
      const minutes = localDate.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    },

    // async generateSlotsLocal() {
    //   // ... (omitted checks) ...

    //   // Prepare params and payload for server assignment
    //   const doctorId = this.doctor?.doctorId || '';
    //   // Convert to UTC HH:mm

    //   const startTimeUtc = this.convertToServerTime(this.currentDay.startTime);
    //   const endTimeUtc = this.convertToServerTime(this.currentDay.endTime);

    //   const params = {
    //     startTime: startTimeUtc,
    //     endTime: endTimeUtc,
    //     doctorId,
    //   };

    //   const payload = {
    //     days: this.selectedDays && this.selectedDays.length ? this.selectedDays : [this.activeDay],
    //     startTime: startTimeUtc,
    //     endTime: endTimeUtc,
    //     doctorId,
    //     appointmentTypeIds: this.selectedAppointmentTypes || [],
    //   };

    //   // ... (omitted params prep) ...

    //   // REPLACING THE TRY/CATCH BLOCK
    //   try {
    //     console.log("Payload:", payload);
    //     const res = await DoctorTimeSlotService.assignTimeslots(params, payload);
    //     console.log("Server response:", res);
    //     const generated = (res && res.data && res.data.data && res.data.data.final) || (res && res.data.data) || [];

    //     console.log("Generated slots:", generated);

    //     // Merge server-generated slots into doctor.days
    //     let updated = [...(this.doctor.days || [])];

    //     if (Array.isArray(generated) && generated.length) {
    //       // Group slots by day
    //       const grouped = {};

    //       generated.forEach((item) => {
    //         const rawDay = item.day || item.dayName || this.activeDay;
    //         const dayName = this.normalizeDay(rawDay);
    //         if (!grouped[dayName]) grouped[dayName] = [];

    //         // Convert UTC to Local time
    //         const startLocalDate = this.convertToLocalTime(null, item.startTime);
    //         const endLocalDate = this.convertToLocalTime(null, item.endTime);

    //         const startLocal = startLocalDate
    //           ? `${startLocalDate.getHours().toString().padStart(2, "0")}:${startLocalDate
    //             .getMinutes()
    //             .toString()
    //             .padStart(2, "0")}`
    //           : "";

    //         const endLocal = endLocalDate
    //           ? `${endLocalDate.getHours().toString().padStart(2, "0")}:${endLocalDate
    //             .getMinutes()
    //             .toString()
    //             .padStart(2, "0")}`
    //           : "";

    //         // Store slots
    //         grouped[dayName].push({
    //           startTime: startLocal,
    //           endTime: endLocal,
    //           id: item.id,
    //           appointmentTypeIds: item.appointmentTypeIds || []
    //         });
    //       });

    //       console.log("Grouped slots:", grouped);

    //       Object.keys(grouped).forEach((dayName) => {
    //         const items = grouped[dayName];


    //         // We need to distribute these items to the correct doctor.days entries based on appointmentType

    //         // First, find all unique appointmentTypeIds involved for this day
    //         // Or better, iterate over all available types in frontend (or just selected ones) and see if they have slots

    //         // Strategy: For each selected appointment type (ID), filter slots that have this ID in their list
    //         // We need to map selected appointment type strings/codes to IDs first?
    //         // The user inputs 'selectedAppointmentTypes' as array of Ids? Or codes?
    //         // In ToggleType and AutoSelect, it pushes `type`. `formatAppointmentType` looks for `t.label === type`.
    //         // It seems 'selectedAppointmentTypes' stores the LABEL or CODE? 
    //         // Wait, user payload provided: "appointmentTypeIds": ["694e...", ...]
    //         // If `this.selectedAppointmentTypes` held IDs, then simpler.
    //         // But `toggleType` pushes `type`. `appointmentTypes` usually have `id`.
    //         // I need to check if `selectedAppointmentTypes` holds strings (names) or IDs.
    //         // User prompt: "i can send multiple appointment ids after select... appointmentTypeIds: this.selectedAppointmentTypes || []".
    //         // This implies `selectedAppointmentTypes` holds IDs.
    //         // But `formatAppointmentType` does `t.label === type`. If type is ID, `t.label === ID` is wrong.
    //         // IF `selectedAppointmentTypes` holds IDs, then `t.id === type` would be correct.
    //         // Let's assume `selectedAppointmentTypes` holds IDs as per the payload requirement.

    //         // If they are IDs, we can match with response.

    //         this.selectedAppointmentTypes.forEach(typeId => {
    //           // Find slots in 'items' that contain this typeId
    //           const typeSlots = items.filter(slot =>
    //             slot.appointmentTypeIds && slot.appointmentTypeIds.includes(typeId)
    //           ).map(s => ({ startTime: s.startTime, endTime: s.endTime, id: s.id }));

    //           // If no slots from server for this type, maybe skip or use all?
    //           // If the server returns appointmentTypeIds, we should respect it.
    //           // If 'appointmentTypeIds' is missing in response item, maybe it applies to all?

    //           const finalSlots = typeSlots.length > 0 ? typeSlots.sort((a, b) => a.startTime.localeCompare(b.startTime)) : [];

    //           if (finalSlots.length === 0) return; // Don't update if no slots return for this type
    //           // We need to store the APPOINTMENT TYPE LABEL/CODE in doctor.days if that's what the UI expects?
    //           // The UI uses `formatAppointmentType` which finds by label?
    //           // If we are strictly using IDs now, we should ensure `doctor.days` stores ID too?
    //           // The `doctor.days` structure: `appointmentType`.
    //           // Let's stick to using the ID as the key in `doctor.days` if that's what we are using.

    //           const appointmentType = typeId;

    //           const idx = updated.findIndex(d => d.day === dayName && d.appointmentType === appointmentType);
    //           const newDay = {
    //             day: dayName,
    //             appointmentType,
    //             startTime: this.currentDay.startTime,
    //             endTime: this.currentDay.endTime,
    //             break: this.currentDay.break,
    //             numberOfPatients: this.currentDay.numberOfPatients,
    //             slots: finalSlots,
    //           };

    //           if (idx !== -1) updated[idx] = newDay; else updated.push(newDay);

    //           console.log("this.selectedAppointmentTypes", this.selectedAppointmentTypes)
    //         });
    //       });
    //     }
    //     console.log("this.selectedAppointmentTypes2", this.selectedAppointmentTypes)

    //     this.$emit('update:doctor', { ...this.doctor, days: updated });
    //     this.$emit('show-toast', { type: 'success', message: `Slots generated from server.` });
    //   } catch (error) {
    //     console.error(error);
    //     this.$emit('show-toast', { type: 'error', message: (error && error.message) || 'Failed to generate slots from server' });
    //   }
    // },

    deleteSelectedDayTypeSlot() {
      const selectedType = this.selectedAppointmentType || "";
      const dayName = this.activeDay;

      if (!confirm(`Clear all slots for ${dayName} - ${selectedType}?`)) return;

      const targetIndex = this.doctor.days.findIndex(
        (d) => d.day === dayName && d.appointmentType === selectedType
      );

      if (targetIndex !== -1) {
        let updatedDays = [...this.doctor.days];
        updatedDays.splice(targetIndex, 1);
        this.$emit("update:doctor", { ...this.doctor, days: updatedDays });
        this.$emit("show-toast", { type: 'success', message: "Slots cleared." });
      }
    },

    updateDayField(field, value) {
      const selectedType = this.selectedAppointmentType || "";
      let updatedDays = [...this.doctor.days];
      const existingIndex = updatedDays.findIndex(
        (d) => d.day === this.activeDay && d.appointmentType === selectedType
      );

      const val = (field === "numberOfPatients" || field === "break") ? Number(value) : value;

      if (existingIndex !== -1) {
        updatedDays[existingIndex] = {
          ...updatedDays[existingIndex],
          [field]: val,
        };
      } else {
        updatedDays.push({
          day: this.activeDay,
          appointmentType: selectedType,
          startTime: field === "startTime" ? val : "",
          endTime: field === "endTime" ? val : "",
          numberOfPatients: field === "numberOfPatients" ? val : 0,
          break: field === "break" ? val : 0,
          slots: [],
        });
      }
      this.$emit("update:doctor", { ...this.doctor, days: updatedDays });
    },

    finish() {
      if (!this.validateStep1()) {
        this.currentStep = 0;
        return;
      }

      this.$emit('show-toast', {
        type: 'success',
        message: 'Schedule configuration completed successfully!'
      });
      this.$emit('finished');

      this.$router.push({ name: 'HospitalSettings' });
    },

    openDetailModal(day) {
      this.selectedDetailDay = day;
      this.showDetailModal = true;
    },

    closeDetailModal() {
      this.showDetailModal = false;
    },

    convertTo12Hour(time24) {
      if (!time24) return "";
      let [hours, minutes] = time24.split(":").map(Number);
      let period = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${hours}:${minutes?.toString()?.padStart(2, "0")} ${period}`;
    },

    normalizeDay(day) {
      if (!day) return '';
      return day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
    },

    timeToMinutes(time) {
      const [hours, minutes] = time.split(":").map(Number);
      return hours * 60 + minutes;
    },

    minutesToTime(minutes) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
    },

    toggleDay(day) {
      const days = [...this.selectedDays];
      const index = days.indexOf(day);

      if (index === -1) {
        days.push(day);
        // if no active day, set first selected as active
        if (!this.activeDay) {
          this.$emit("update:active-day", day);
        }
      } else {
        days.splice(index, 1);
        // if removing active day, switch active
        if (this.activeDay === day) {
          this.$emit("update:active-day", days[0] || "");
        }
      }

      this.$emit("update:selected-days", days);
    },
  },
};
</script>

<style scoped>
/* Add these styles to your existing CSS */

.timeslot-stepper-container {
  --primary-dark: #0144A6;
  --primary-bright: #219DFF;
  --accent-yellow: #ffba33;
  --text-grey: #606060;
  --bg-light: #f4f6fa;
  --bg-white: #ffffff;
  --border-radius-card: 20px;
  --border-radius-btn: 30px;
  --border-radius-input: 40px;
  --font-family: 'Poppins', sans-serif;

  font-family: var(--font-family);
  display: flex;
  flex-direction: column;
  gap: 25px;
  max-width: 1100px;
  margin: 0 auto;
}

/* CONVERSATION HEADER */
.conversation-header {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  padding: 0 15px;
  margin-bottom: 10px;
}

.avatar-assistant {
  width: 50px;
  height: 50px;
  background: var(--primary-bright);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 10px rgba(33, 157, 255, 0.3);
  flex-shrink: 0;
}

/* STEPPER HEADER */
.stepper-header-modern {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
}

.step-item {
  display: flex;
  align-items: center;
  flex: 1;
  cursor: pointer;
  opacity: 0.5;
  transition: all 0.3s ease;
}

.step-item.active,
.step-item.completed {
  opacity: 1;
}

.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  border: 2px solid #e0e0e0;
  color: var(--text-grey);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  z-index: 2;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.step-item.active .step-circle {
  background: var(--primary-bright);
  color: #fff;
  border-color: var(--primary-bright);
  box-shadow: 0 0 0 5px rgba(0, 123, 255, 0.2);
  transform: scale(1.1);
}

.step-item.completed .step-circle {
  background: var(--primary-dark);
  color: #fff;
  border-color: var(--primary-dark);
}

.step-label {
  margin-left: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--primary-dark);
  white-space: nowrap;
}

.step-line {
  flex: 1;
  height: 3px;
  background: #e0e0e0;
  margin: 0 15px;
  border-radius: 2px;
}

.step-item.completed .step-line {
  background: var(--primary-dark);
}

.assistant-bubble {
  background: white;
  padding: 15px 25px;
  border-radius: 0 20px 20px 20px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  font-size: 1.1rem;
  color: var(--primary-dark);
  font-weight: 500;
  line-height: 1.5;
  position: relative;
  border: 1px solid #eef2f6;
  max-width: 800px;
}

.assistant-bubble::after {
  content: '';
  position: absolute;
  top: 0;
  left: -10px;
  width: 0;
  height: 0;
  border-top: 15px solid white;
  border-left: 10px solid transparent;
}

/* Message Animation */
.message-fade-enter-active,
.message-fade-leave-active {
  transition: all 0.3s ease;
}

.message-fade-enter-from {
  opacity: 0;
  transform: translateY(5px);
}

.message-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* CONTENT CARD (DECK) */
.step-content-card {
  background: var(--bg-white);
  border-radius: var(--border-radius-card);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  padding: 40px;
  min-height: 450px;
  border: 1px solid rgba(28, 52, 118, 0.05);
  /* Make it feel like the response area */
  border-top: 4px solid var(--primary-bright);
}

.step-heading {
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-dark);
  margin-bottom: 8px;
}

.step-subheading {
  color: var(--text-grey);
  font-size: 1rem;
  margin-bottom: 35px;
  font-weight: 400;
}

/* FORMS & INPUTS */
.form-card-internal {
  /* background: #fbfbfb; */
  /* padding: 10px; */
}

.form-label-theme {
  display: block;
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--text-grey);
  font-size: 1.05rem;
}

.input-theme-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.form-control-theme {
  width: 100%;
  padding: 16px 20px 16px 50px;
  border: 3px solid var(--primary-dark);
  border-radius: var(--border-radius-input);
  font-size: 1rem;
  color: #060606;
  font-weight: 500;
  transition: all 0.2s;
  background: #fff;
  font-family: var(--font-family);
}

.form-control-theme:focus {
  border-color: var(--primary-bright);
  outline: none;
  box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.1);
}

.icon-indicator {
  position: absolute;
  left: 20px;
  color: var(--primary-dark);
  font-size: 1.2rem;
  pointer-events: none;
}

/* STEP 2: DAYS & TYPES */
.section-label {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--primary-dark);
  margin-bottom: 15px;
  display: block;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 15px;
}

.day-card {
  border: 2px solid #e0e0e0;
  border-radius: 15px;
  /* padding: 10px 10px; */
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  background: #fff;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02);
}

.day-card:hover {
  border-color: var(--primary-bright);
  transform: translateY(-3px);
  box-shadow: 0 8px 15px rgba(0, 123, 255, 0.15);
}

.day-card.selected {
  border-color: var(--primary-bright);
  background: #f0f5ff;
}

.day-name {
  font-weight: 600;
  color: var(--text-grey);
  font-size: 1rem;
}

.day-card.selected .day-name {
  color: var(--primary-dark);
  font-weight: 700;
}

.day-check-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  color: var(--primary-bright);
  font-size: 1.1rem;
}

.day-dot {
  position: absolute;
  bottom: 10px;
  width: 6px;
  height: 6px;
  background: var(--accent-yellow);
  border-radius: 50%;
}

.types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

.type-card {
  display: flex;
  align-items: center;
  padding: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s;
  background: white;
  position: relative;
  overflow: hidden;
}

.type-card:hover {
  border-color: var(--primary-bright);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
}

.type-card.active {
  border-color: var(--primary-bright);
  background: linear-gradient(145deg, #ffffff, #f0f5ff);
}

.type-card.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: var(--primary-bright);
}

.type-icon-wrapper {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: #f4f6fa;
  color: var(--primary-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  margin-right: 15px;
}

.type-card.active .type-icon-wrapper {
  background: var(--primary-bright);
  color: white;
}

.type-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.type-name {
  font-weight: 700;
  font-size: 1rem;
  color: var(--primary-dark);
}

.type-sub {
  font-size: 0.8rem;
  color: var(--primary-bright);
  font-weight: 600;
}

.custom-checkbox {
  width: 24px;
  height: 24px;
  border: 2px solid #ccc;
  border-radius: 6px;
  position: relative;
  transition: all 0.2s;
}

.custom-checkbox.checked {
  background: var(--primary-bright);
  border-color: var(--primary-bright);
}

.custom-checkbox.checked::after {
  content: '\f00c';
  font-family: 'Font Awesome 6 Free';
  font-weight: 900;
  color: white;
  font-size: 14px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* ALERTS */
.premium-alert {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  border-radius: 15px;
  margin-bottom: 20px;
  gap: 15px;
}

.premium-alert.warning {
  background: #fff5e6;
  border: 1px solid #ffeeba;
  color: #856404;
}

.premium-alert.info {
  background: aliceblue;
  border: 1px solid #daeaff;
  color: var(--primary-dark);
}

.alert-icon {
  font-size: 1.5rem;
  opacity: 0.8;
}

.alert-content {
  flex: 1;
  font-size: 0.95rem;
  display: flex;
  flex-direction: column;
}

.btn-theme-outline-danger {
  background: transparent;
  border: 2px solid #dc3545;
  color: #dc3545;
  border-radius: 30px;
  padding: 6px 20px;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-theme-outline-danger:hover {
  background: #dc3545;
  color: white;
}

/* STEP 3 CONFIG */
.glass-panel {
  background: #ffffff;
  border: 1px solid #eee;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
}

.panel-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--primary-dark);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.badge-count {
  background: var(--primary-bright);
  color: white;
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 10px;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.btn-theme-primary {
  background: var(--primary-dark);
  color: #fff;
  border: none;
  font-family: var(--font-family);
  font-weight: 600;
  padding: 12px 30px;
  border-radius: var(--border-radius-btn);
  font-size: 1rem;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.btn-theme-primary:hover:not(:disabled) {
  background: #142555;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(28, 52, 118, 0.3);
}

.btn-theme-secondary {
  background: #eef2f6;
  color: var(--primary-dark);
  border: none;
  font-family: var(--font-family);
  font-weight: 600;
  padding: 12px 30px;
  border-radius: var(--border-radius-btn);
  font-size: 1rem;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.btn-theme-secondary:hover:not(:disabled) {
  background: #dbe4ef;
  color: var(--primary-dark);
}

.btn-theme-primary:disabled,
.btn-theme-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.actions-group {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
}

/* .slots-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
} */

.slots-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
}

.slot-pill {
  justify-content: space-between;
  width: 100%;
  max-width: 270px;
}

.slot-pill {
  background: #ffffff;
  border: 2px solid #e0e0e0;
  border-radius: 30px;
  padding: 8px 18px;
  font-weight: 600;
  color: var(--text-grey);
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.2s;
}

.slot-pill:hover {
  border-color: var(--primary-bright);
  color: var(--primary-dark);
}

.slot-delete {
  color: #ff6b6b;
  cursor: pointer;
  padding: 2px;
}

.slot-delete:hover {
  color: #fa5252;
  transform: scale(1.1);
}

/* STEP 4: REVIEW & ADVANCED */
.advanced-box {
  background: #f8fbff;
  border: 2px dashed #dbe4ef;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s;
}

.advanced-box:hover {
  border-color: var(--primary-bright);
}

.advanced-header {
  padding: 20px 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: var(--primary-dark);
  transition: background 0.3s;
}

.advanced-header:hover {
  background: #f1f6fc;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-bright);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  font-size: 1.1rem;
}

.header-text-group {
  display: flex;
  flex-direction: column;
}

.header-text-group .title {
  font-size: 1rem;
  font-weight: 700;
}

.header-text-group .subtitle {
  font-size: 0.85rem;
  color: var(--text-grey);
  font-weight: 400;
}

.toggle-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--primary-bright);
  font-size: 0.9rem;
}

.advanced-content {
  padding: 0 30px 30px 30px;
}

.guide-text {
  background: #fff;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-left: 4px solid var(--accent-yellow);
  border-radius: 8px;
  margin-bottom: 25px;
  font-size: 0.95rem;
  color: var(--text-grey);
  display: flex;
  gap: 12px;
  align-items: center;
}

.guide-text i {
  color: var(--accent-yellow);
  font-size: 1.2rem;
}

.form-select-theme {
  width: 100%;
  padding: 12px;
  border: 2px solid #dbe4ef;
  border-radius: 12px;
  color: var(--text-grey);
  font-family: var(--font-family);
  font-size: 1rem;
}

.mini-days-grid span,
.mini-types-grid span {
  display: inline-block;
  padding: 6px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  margin-right: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.mini-days-grid span:hover,
.mini-types-grid span:hover {
  border-color: var(--primary-bright);
}

.mini-days-grid span.active,
.mini-types-grid span.active {
  background: var(--primary-bright);
  color: white;
  border-color: var(--primary-bright);
  box-shadow: 0 4px 10px rgba(0, 123, 255, 0.2);
}

.review-grid-modern {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}

.review-card {
  background: white;
  border: 1px solid #eee;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  transition: all 0.3s;
}

.review-card.clickable {
  cursor: pointer;
}

.review-card.clickable:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 123, 255, 0.15);
  border-color: var(--primary-bright);
}

.review-card-header {
  background: var(--bg-light);
  padding: 12px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  color: var(--primary-dark);
}

.type-badge {
  background: white;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 0.75rem;
  color: var(--primary-bright);
  font-weight: 700;
}

.review-card-body {
  padding: 15px;
}

.slot-count {
  font-weight: 700;
  color: var(--text-grey);
  margin-bottom: 5px;
}

.slot-count.success {
  color: #20c997;
}

/* STEPPER NAV BUTTONS */
.stepper-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
}

.btn-theme-nav {
  border: none;
  font-family: var(--font-family);
  font-weight: 600;
  padding: 14px 35px;
  border-radius: var(--border-radius-btn);
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-theme-nav.next,
.btn-theme-nav.finish {
  background: var(--primary-bright);
  color: white;
  box-shadow: 0 10px 20px rgba(0, 123, 255, 0.2);
}

.btn-theme-nav.next:hover,
.btn-theme-nav.finish:hover {
  background: #0069d9;
  transform: translateY(-2px);
  box-shadow: 0 15px 25px rgba(0, 123, 255, 0.35);
}

.btn-theme-nav.next:disabled {
  background: #0069d9;
  transform: translateY(-2px);
  box-shadow: 0 15px 25px rgba(0, 123, 255, 0.35);
}

.btn-theme-nav.finish {
  background: var(--primary-dark);
  transform: translateY(0px);
  box-shadow: none;
}

.btn-theme-nav.finish:hover {
  background: #142555;
  box-shadow: 0 15px 25px rgba(28, 52, 118, 0.3);
}

.btn-theme-nav.prev {
  background: #eef2f6;
  color: var(--text-grey);
}

.btn-theme-nav.prev:hover {
  background: #dbe4ef;
  color: var(--primary-dark);
}

/* MODAL */
.slot-detail-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.slot-detail-modal {
  background: white;
  width: 90%;
  max-width: 800px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease-out;
}

.modal-header-theme {
  background: var(--primary-dark);
  padding: 15px 25px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.close-btn {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.close-btn:hover {
  opacity: 1;
}

.modal-body-theme {
  padding: 25px;
  max-height: 60vh;
  overflow-y: auto;
}

.modal-slots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
}

.modal-slot-item {
  background: #f4f6fa;
  border: 1px solid #e0e0e0;
  padding: 8px;
  text-align: center;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--primary-dark);
}

.modal-footer-theme {
  padding: 15px 25px;
  border-top: 1px solid #eee;
  text-align: right;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* UTILITIES */
.highlight-text {
  color: var(--primary-dark);
  font-weight: 700;
}

.highlight-text-light {
  color: var(--primary-bright);
  font-weight: 600;
}

.error-box {
  background: #fff8f8;
  color: #e03131;
  padding: 15px;
  border-radius: 12px;
  border: 1px solid #ffc9c9;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
}

.rotated {
  transform: rotate(180deg);
}

.transition-icon {
  transition: transform 0.3s ease;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #adb5bd;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 15px;
  opacity: 0.5;
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .step-label {
    display: none;
  }

  .config-grid {
    grid-template-columns: 1fr 1fr;
  }

  .stepper-actions {
    padding: 0 10px;
  }

  .step-content-card {
    padding: 25px;
  }

  .conversation-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .assistant-bubble::after {
    top: -10px;
    left: 20px;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 15px solid white;
    border-top: none;
  }
}

.auto-select-section {
  background: #f8fbff;
  border: 1px solid #dbe4ef;
  border-radius: 12px;
  padding: 15px;
  margin-bottom: 20px;
}

.btn-theme-outline-primary {
  background: transparent;
  border: 2px solid var(--primary-bright);
  color: var(--primary-bright);
  border-radius: 30px;
  padding: 8px 20px;
  font-weight: 600;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
}

.btn-theme-outline-primary:hover {
  background: var(--primary-bright);
  color: white;
}

.btn-theme-success {
  background: #20c997;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 12px 25px;
  font-weight: 600;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-theme-success:hover:not(:disabled) {
  background: #199d76;
  transform: translateY(-2px);
}

.btn-theme-success:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-theme-sm {
  background: var(--primary-bright);
  color: white;
  border: none;
  border-radius: 20px;
  padding: 6px 15px;
  font-size: 0.85rem;
  font-weight: 600;
}

.day-card.auto-selected {
  border-color: var(--accent-yellow);
  background: #fffcf0;
}

.type-card.auto-selected {
  border-color: var(--accent-yellow);
}

.auto-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  color: var(--accent-yellow);
  font-size: 0.9rem;
}

.slot-duration {
  font-size: 0.8rem;
  color: #666;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 10px;
  /* margin-left: 10px; */
}

.slot-duration-badge {
  font-size: 0.75rem;
  color: #666;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 8px;
  /* margin-left: 8px; */
}

.premium-alert.success {
  background: #e6fff2;
  border: 1px solid #b3f0d6;
  color: #0d6e42;
}

.bulk-actions-section {
  margin-bottom: 25px;
}

.actions-group {
  display: flex;
  gap: 15px;
  justify-content: flex-start;
  flex-wrap: wrap;
}

/* Ensure buttons have proper spacing on mobile */
@media (max-width: 768px) {
  .actions-group {
    flex-direction: column;
  }

  .actions-group button {
    width: 100%;
    justify-content: center;
  }
}

.active-badge {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: #219dff;
  color: rgb(13, 239, 43);
  font-size: 10px;
  padding: 2px 4px;
  border-radius: 50%;
}

.btn-generate {
  margin-top: 30px;
}

/* Grouped Summary Styles */
.grouped-card .review-card-header {
  background: var(--bg-light);
  border-bottom: 1px solid #eee;
}

.pill-badge {
  border-radius: 50rem;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
}

.type-slot-row {
  padding: 10px 15px;
}

.type-slot-row:last-child {
  border-bottom: none;
}

.btn-icon-sm {
  background: transparent;
  border: 1px solid #e0e0e0;
  /* Subtle border */
  color: var(--primary-bright);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  /* Circle */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.8rem;
}

.btn-icon-sm:hover {
  background: var(--primary-bright);
  color: white;
  border-color: var(--primary-bright);
}
</style>